import React, { useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, STATUSES } from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Upload, Trash2, File as FileIcon, Send, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";
import PizzaTracker from "@/components/app/PizzaTracker";
import AdminChecklist from "@/components/app/AdminChecklist";
import AuditTimeline from "@/components/app/AuditTimeline";
import DocPreviewDialog from "@/components/app/DocPreviewDialog";
import AdminFundingControls from "@/components/app/AdminFundingControls";
import ScheduleClientDialog from "@/components/app/ScheduleClientDialog";
import ClientScheduleAdmin from "@/components/app/ClientScheduleAdmin";
import SendSMSDialog from "@/components/app/SendSMSDialog";
import AdminCreditReportPanel from "@/components/app/AdminCreditReportPanel";
import { useAuth } from "@/context/AuthContext";

export default function AdminClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [team, setTeam] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [msgText, setMsgText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/clients/${id}`);
    setClient(data);
    setForm({
      name: data.name, email: data.email, phone: data.phone || "",
      start_date: data.start_date ? data.start_date.slice(0, 10) : "",
      status: data.status, assigned_team_member_id: data.assigned_team_member_id || "",
    });
    const m = await api.get(`/admin/clients/${id}/messages`);
    setMsgs(m.data);
  };

  useEffect(() => {
    api.get("/admin/team-members").then(({ data }) => setTeam(data));
    load(); /* eslint-disable-next-line */
  }, [id]);

  if (!client) return <AppShell><div className="text-slate-500">Loading…</div></AppShell>;

  const save = async () => {
    const payload = {
      ...form,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      assigned_team_member_id: form.assigned_team_member_id || null,
    };
    await api.put(`/admin/clients/${id}`, payload);
    toast.success("Saved");
    load();
  };

  const changeStatus = async (v) => {
    await api.put(`/admin/clients/${id}`, { status: v });
    toast.success(`Status → ${v}`);
    load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/admin/clients/${id}/notes`, { text: noteText });
    setNoteText("");
    load();
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/clients/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    await api.post(`/admin/clients/${id}/messages`, { text: msgText });
    setMsgText("");
    const m = await api.get(`/admin/clients/${id}/messages`);
    setMsgs(m.data);
  };

  const del = async () => {
    await api.delete(`/admin/clients/${id}`);
    toast.success("Client deleted");
    nav("/admin");
  };

  return (
    <AppShell>
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="back-btn"><ArrowLeft size={16} className="mr-1" /> Back</Button>
      </div>

      <div className="cq-in">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Client Profile</p>
            <h1 className="font-display text-4xl font-black text-slate-900" data-testid="client-header-name">{client.name}</h1>
            <p className="text-slate-500 mt-1">{client.email} · {client.phone || "no phone"}</p>
          </div>
          {user?.role === "admin" && (
            <div className="flex items-center gap-2">
              <SendSMSDialog client={client} actor={user} />
              <ScheduleClientDialog client={client} team={team} onDone={load} />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-client-btn"><Trash2 size={16} className="mr-1" /> Delete</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete this client?</AlertDialogTitle>
                  <AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          {user?.role !== "admin" && user && (
            <div className="flex items-center gap-2">
              <SendSMSDialog client={client} actor={user} />
              <ScheduleClientDialog client={client} team={team} onDone={load} />
            </div>
          )}
        </div>

        <Card className="p-6 rounded-2xl border-slate-100 mb-6">
          <PizzaTracker status={client.effective_status} startDate={client.start_date} />
        </Card>

        <div className="mb-6">
          <AdminChecklist clientId={client.id} checklist={client.checklist || []} onChange={load} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: profile + status */}
          <Card className="p-6 rounded-2xl border-slate-100 lg:col-span-1">
            <h3 className="font-display font-bold text-lg mb-4">Details</h3>
            <div className="space-y-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={changeStatus}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase().replace(/ /g, "-")}`}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {client.effective_status === "Completed After 30 Days" && !client.manual_status_override && (
                  <p className="text-xs text-amber-600 mt-2">Auto-set: 30+ days since start. Change status to override.</p>
                )}
              </div>
              <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></div>
              <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" /></div>
              <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="edit-start-date" /></div>
              <div>
                <Label>Assigned team member</Label>
                <Select value={form.assigned_team_member_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_team_member_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={save} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="save-client-btn"><Save size={16} className="mr-1" /> Save</Button>
              <div className="pt-3 mt-3 border-t border-slate-100 text-xs text-slate-500">
                <p>Estimated completion: <span className="font-semibold text-slate-800">{fmtDate(client.estimated_completion)}</span></p>
              </div>
            </div>
          </Card>

          {/* Middle: notes */}
          <Card className="p-6 rounded-2xl border-slate-100 lg:col-span-1">
            <h3 className="font-display font-bold text-lg mb-4">Internal notes</h3>
            <div className="space-y-3">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Type a note…" data-testid="note-textarea" />
              <Button onClick={addNote} variant="outline" className="w-full" data-testid="add-note-btn">Add note</Button>
              <div className="space-y-3 mt-4 max-h-96 overflow-y-auto">
                {(client.notes || []).slice().reverse().map((n) => (
                  <div key={n.id} className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm">
                    <p className="text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="text-xs text-slate-500 mt-2">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
                {(!client.notes || client.notes.length === 0) && <p className="text-sm text-slate-400">No notes yet.</p>}
              </div>
            </div>
          </Card>

          {/* Right: docs + messages */}
          <Card className="p-6 rounded-2xl border-slate-100 lg:col-span-1">
            <h3 className="font-display font-bold text-lg mb-3">Documents</h3>
            <label className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center hover:border-amber-300 hover:bg-amber-50 transition" data-testid="upload-dropzone">
              <Upload className="mx-auto text-slate-400" size={22} />
              <p className="text-sm text-slate-600 mt-1">{uploading ? "Uploading…" : "Click or drop to upload"}</p>
              <input type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="upload-input" />
            </label>
            <div className="mt-4 space-y-2">
              {(client.documents || []).map((d) => (
                <button key={d.id} onClick={() => setPreviewDoc(d)} className="w-full text-left flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-3 hover:bg-slate-50" data-testid={`doc-${d.id}`}>
                  <FileIcon size={16} className="text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{d.filename}</p>
                    <p className="text-xs text-slate-500">{(d.size / 1024).toFixed(1)} KB · {d.source} · {new Date(d.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge variant="outline">{d.source}</Badge>
                </button>
              ))}
              {(!client.documents || client.documents.length === 0) && <p className="text-sm text-slate-400">No documents.</p>}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <h3 className="font-display font-bold text-lg mb-3">Messages with client</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1 mb-3" data-testid="messages-list">
                {msgs.map((m) => (
                  <div key={m.id} className={`rounded-xl p-3 text-sm ${m.sender_role === "client" ? "bg-slate-100" : "bg-sky-100"}`}>
                    <p className="text-slate-800 whitespace-pre-wrap">{m.text}</p>
                    <p className="text-xs text-slate-500 mt-1">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</p>
                  </div>
                ))}
                {msgs.length === 0 && <p className="text-sm text-slate-400">No messages yet.</p>}
              </div>
              <div className="flex gap-2">
                <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Reply to client…" data-testid="admin-msg-input" onKeyDown={(e) => e.key === "Enter" && sendMsg()} />
                <Button onClick={sendMsg} className="bg-sky-500 hover:bg-sky-600" data-testid="admin-msg-send"><Send size={16} /></Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-6">
          <ClientScheduleAdmin clientId={client.id} team={team} onChange={load} />
        </div>

        <div className="mt-6">
          <AdminCreditReportPanel clientId={client.id} />
        </div>

        <div className="mt-6">
          <AuditTimeline endpoint={`/admin/clients/${client.id}/timeline`} refreshKey={client.updated_at} />
        </div>

        {client.user_id && (
          <div className="mt-6">
            <AdminFundingControls userId={client.user_id} />
          </div>
        )}

        <DocPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(v) => !v && setPreviewDoc(null)} />
      </div>
    </AppShell>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
}
